/**
 * Содержимое боковой панели: две вкладки «Нарративы» и «Справочник».
 *
 *   - «Нарративы»    — TOC по project-нарративам + Канвасам
 *   - «Справочник» — глоссарий (типы scoped к Канвасу/svcflow)
 *
 * Сама панель — это **только контент** Tabs'ов. Wrapper (sticky aside
 * на десктопе или Mantine `<Drawer>` на mobile) живёт в `CanvasViewPage`.
 *
 * Click по anchor'у скроллит к цели + триггерит flash-подсветку через
 * `ui-store.flashAnchor`; на mobile дополнительно закрывает sidebar.
 */

import {
    Accordion, Anchor, Badge, Box, Group, Loader, ScrollArea, SegmentedControl,
    Stack, Tabs, Text, TextInput, ThemeIcon,
} from '@mantine/core';
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { IconCheck, IconX, IconArrowRight } from '@tabler/icons-react';
import type {
    CanvasView, Glossary, GlossaryGroup, NarrativeAspect,
    ProjectNarrativeAspectId, SignalFeedEntry,
} from '@pflow/shared/v2';
import { FRAME_IDS } from '@pflow/shared/v2';
import { NARRATIVE_ASPECT_META } from '../lib/narrative-meta';
import { fetchComments, fetchIssues } from '../lib/api-client';
import { anchorId } from './NarrativeAspectView';
import { useUiStore } from '../stores/ui-store';

interface Props {
    projectAspects: NarrativeAspect[];
    canvases:       CanvasView[];
    glossary:       Glossary;
    /** Лента Сигналов из снапшота (новые сверху); вкладка прячется, если нет. */
    signals?:       SignalFeedEntry[];
    /** Для вкладки «Вопросы» (открытые GitHub issues). */
    owner?:         string;
    repo?:          string;
    /** Поддиректория проекта в монорепо — скоупит «Вопросы» по проекту. */
    path?:          string;
    /** На каждый клик по anchor'у. Wrapper закрывает mobile-drawer. */
    onNavigate?:    () => void;
    /** Высота scroll-area (отличается desktop vs drawer). */
    scrollHeight?:  string;
}

export function GlossarySidebar({
    projectAspects, canvases, glossary, signals, owner, repo, path, onNavigate,
    scrollHeight = 'calc(100vh - 110px)',
}: Props): JSX.Element {
    const [tab, setTab] = useState<string>('chapters');
    const hasSignals = (signals?.length ?? 0) > 0;
    const hasIssues  = !!owner && !!repo;
    return (
        <Tabs value={tab} onChange={v => setTab(v ?? 'chapters')} keepMounted={false}>
            <Tabs.List grow data-tour="sidebar-tabs">
                <Tabs.Tab value="chapters">Нарративы</Tabs.Tab>
                <Tabs.Tab value="entities">Справочник</Tabs.Tab>
                {hasSignals && <Tabs.Tab value="signals">Сигналы</Tabs.Tab>}
                {hasIssues  && <Tabs.Tab value="issues">Вопросы</Tabs.Tab>}
            </Tabs.List>
            <Tabs.Panel value="chapters">
                <ScrollArea h={scrollHeight} type="hover">
                    <ChaptersTab
                        projectAspects={projectAspects}
                        canvases={canvases}
                        onNavigate={onNavigate}
                    />
                </ScrollArea>
            </Tabs.Panel>
            <Tabs.Panel value="entities">
                <ScrollArea h={scrollHeight} type="hover">
                    <EntitiesTab glossary={glossary} onNavigate={onNavigate}
                        owner={owner} repo={repo} path={path} />
                </ScrollArea>
            </Tabs.Panel>
            {hasSignals && (
                <Tabs.Panel value="signals">
                    <ScrollArea h={scrollHeight} type="hover">
                        <SignalsFeedTab signals={signals!} onNavigate={onNavigate} />
                    </ScrollArea>
                </Tabs.Panel>
            )}
            {hasIssues && (
                <Tabs.Panel value="issues">
                    <ScrollArea h={scrollHeight} type="hover">
                        <IssuesTab owner={owner!} repo={repo!} path={path} />
                    </ScrollArea>
                </Tabs.Panel>
            )}
        </Tabs>
    );
}

// ─── вкладка «Сигналы» — лента «что нового узнали» ──────────────────────

const VECTOR_UI: Record<SignalFeedEntry['vector'], { icon: JSX.Element; color: string }> = {
    refutes:  { icon: <IconX size={12} />,          color: 'red' },
    confirms: { icon: <IconCheck size={12} />,      color: 'green' },
    related:  { icon: <IconArrowRight size={12} />, color: 'gray' },
    none:     { icon: <IconArrowRight size={12} />, color: 'yellow' },
};

function SignalsFeedTab({ signals, onNavigate }: {
    signals: SignalFeedEntry[];
    onNavigate?: () => void;
}): JSX.Element {
    const openCard = useUiStore(s => s.openCard);
    const [category, setCategory] = useState<string>('все');
    const categories = ['все', ...new Set(signals.map(s => s.category).filter((c): c is string => !!c))];
    const visible = category === 'все' ? signals : signals.filter(s => s.category === category);

    return (
        <Stack gap={8} p="sm">
            {categories.length > 2 && (
                <SegmentedControl
                    size="xs"
                    fullWidth
                    value={category}
                    onChange={setCategory}
                    data={categories}
                />
            )}
            {visible.map(s => {
                const v = VECTOR_UI[s.vector];
                return (
                    <Box
                        key={s.name}
                        component="button"
                        type="button"
                        onClick={() => { openCard({ type: 'Сигнал', name: s.name }); onNavigate?.(); }}
                        className="pflow-entity-trigger"
                        style={{
                            appearance: 'none', background: 'none', border: 'none',
                            textAlign: 'left', cursor: 'pointer', color: 'inherit',
                            padding: '4px 6px', borderRadius: 4, display: 'block', width: '100%',
                        }}
                    >
                        <Group gap={6} wrap="nowrap" align="flex-start">
                            <ThemeIcon size="xs" variant="light" color={v.color} radius="xl" style={{ marginTop: 2, flexShrink: 0 }}>
                                {v.icon}
                            </ThemeIcon>
                            <Box style={{ minWidth: 0 }}>
                                <Text fz="xs" fw={500} style={{ wordBreak: 'break-word' }}>{s.name}</Text>
                                <Group gap={6} wrap="wrap">
                                    {s.date && <Text fz="xs" c="dimmed">{s.date}</Text>}
                                    {s.category && <Text fz="xs" c="dimmed">· {s.category}</Text>}
                                    {s.vector === 'none' && <Text fz="xs" c="yellow.7">· без цели</Text>}
                                </Group>
                            </Box>
                        </Group>
                    </Box>
                );
            })}
            {visible.length === 0 && <Text fz="sm" c="dimmed">Нет сигналов этой категории.</Text>}
        </Stack>
    );
}

// ─── вкладка «Вопросы» — открытые GitHub issues ─────────────────────────

function IssuesTab({ owner, repo, path }: { owner: string; repo: string; path?: string }): JSX.Element {
    const issuesQ = useQuery({
        queryKey: ['repo-issues', owner, repo, path],
        queryFn:  () => fetchIssues({ owner, repo, path }),
        retry:    false,
        staleTime: 60_000,
    });

    if (issuesQ.isLoading) return <Box p="sm"><Loader size="xs" /></Box>;
    if (issuesQ.error) {
        return <Box p="sm"><Text fz="xs" c="dimmed">Не удалось загрузить issues: {(issuesQ.error as Error).message}</Text></Box>;
    }
    const issues = issuesQ.data?.issues ?? [];

    return (
        <Stack gap={8} p="sm">
            <Group justify="space-between" wrap="nowrap">
                <Text fz="xs" c="dimmed">Открытых: {issues.length}</Text>
                <Anchor
                    href={newIssueUrl(owner, repo, path)}
                    target="_blank" rel="noreferrer" fz="xs"
                >
                    + новая идея
                </Anchor>
            </Group>
            {issues.length === 0 && <Text fz="sm" c="dimmed">Открытых вопросов нет.</Text>}
            {issues.map(i => (
                <Box key={i.number} style={{ padding: '4px 6px', borderRadius: 4 }}>
                    <Anchor href={i.url} target="_blank" rel="noreferrer" fz="xs" fw={500} c="inherit"
                        style={{ wordBreak: 'break-word', display: 'block' }}>
                        #{i.number} {i.title}
                    </Anchor>
                    <Group gap={4} mt={2} wrap="wrap">
                        {i.labels.map(l => (
                            <Badge key={l} size="xs" variant="light" style={{ textTransform: 'none' }}>{l}</Badge>
                        ))}
                        <Text fz="xs" c="dimmed">{i.author} · {i.createdAt.slice(0, 10)}</Text>
                    </Group>
                </Box>
            ))}
        </Stack>
    );
}

/**
 * URL формы нового issue. В мультипроектном репо префиллим body маркером
 * `<!-- pflow-path -->`, чтобы issue был виден только в своём проекте.
 */
function newIssueUrl(owner: string, repo: string, path?: string): string {
    const u = new URL(`https://github.com/${owner}/${repo}/issues/new`);
    u.searchParams.set('labels', 'idea');
    if (path) u.searchParams.set('body', `\n\n<!-- pflow-path: ${path} -->`);
    return u.toString();
}

// ─── вкладка «Нарративы» ────────────────────────────────────────────────

/**
 * Оглавление обязано повторять порядок страницы, иначе навигатор врёт:
 * «О проекте» (FRAME_IDS) → Канвасы → «Поддержка» (остальные
 * project-аспекты). Тот же split — в `CanvasViewPage` и `PrintPage`,
 * источник правды один — `FRAME_IDS` из `@pflow/shared/v2`.
 */
function ChaptersTab({ projectAspects, canvases, onNavigate }: {
    projectAspects: NarrativeAspect[];
    canvases:       CanvasView[];
    onNavigate?:    () => void;
}): JSX.Element {
    const isFrame   = (a: NarrativeAspect): boolean =>
        FRAME_IDS.has(a.id as ProjectNarrativeAspectId);
    const frame     = projectAspects.filter(isFrame);
    const backstage = projectAspects.filter(a => !isFrame(a));

    const link = (a: NarrativeAspect, keySuffix = ''): JSX.Element => (
        <AnchorLink
            key={a.id + keySuffix}
            anchor={anchorId(a)}
            icon={iconForStatus(a)}
            label={NARRATIVE_ASPECT_META.get(a.id)?.title ?? a.id}
            onNavigate={onNavigate}
        />
    );

    return (
        <Stack gap={4} p="sm">
            {frame.length > 0 && <SectionHeader title="О проекте" />}
            {frame.map(a => link(a))}
            {canvases.map(c => (
                <Box key={c.name} mt={8}>
                    <SectionHeader title={`Канвас «${c.name}»`} />
                    {c.aspects.map(a => link(a, c.name))}
                </Box>
            ))}
            {backstage.length > 0 && (
                <Box mt={8}>
                    <SectionHeader title="Поддержка" />
                    {backstage.map(a => link(a))}
                </Box>
            )}
        </Stack>
    );
}

function iconForStatus(a: NarrativeAspect): string {
    if (!a.summary) return '?';
    return a.summary.status === 'fresh' ? '✨' : '⚠️';
}

// ─── вкладка «Справочник» ─────────────────────────────────────────────

/** Режим фильтра по принадлежности: наши сущности / research-модели конкурентов / все. */
type OwnershipMode = 'ours' | 'market' | 'both';

function EntitiesTab({ glossary, onNavigate, owner, repo, path }: {
    glossary: Glossary; onNavigate?: () => void;
    owner?: string; repo?: string; path?: string;
}): JSX.Element {
    const [filter, setFilter] = useState('');
    const [mode, setMode] = useState<OwnershipMode>('both');
    const f = filter.trim().toLowerCase();

    // Открытые обсуждения по сущностям — бейдж 💬 на строках.
    const commentsQ = useQuery({
        queryKey:  ['all-comments', owner, repo, path],
        queryFn:   () => fetchComments({ owner: owner!, repo: repo!, path }),
        enabled:   !!owner && !!repo,
        staleTime: 60_000,
        refetchInterval:      60_000,
        refetchOnWindowFocus: true,
        retry:     false,
    });
    const discussCounts = new Map<string, number>();
    for (const c of commentsQ.data?.comments ?? []) {
        if (!c.target) continue;
        discussCounts.set(c.target, (discussCounts.get(c.target) ?? 0) + 1);
    }

    // Фильтр Наш/Рынок показываем только если рыночные сущности вообще есть —
    // в проекте без разобранных конкурентов переключатель был бы шумом.
    const hasMarket = [glossary.pflow, glossary.svcflow, glossary.dflow]
        .some(gs => gs.some(g => g.items.some(i => i.market)));

    const visiblePflow   = filterGroups(glossary.pflow,   f, mode);
    const visibleSvcflow = filterGroups(glossary.svcflow, f, mode);
    const visibleDflow   = filterGroups(glossary.dflow,   f, mode);

    return (
        <Stack gap={6} p="sm">
            <TextInput
                placeholder="Поиск по имени…"
                size="xs"
                value={filter}
                onChange={e => setFilter(e.currentTarget.value)}
            />
            {hasMarket && (
                <SegmentedControl
                    size="xs"
                    fullWidth
                    value={mode}
                    onChange={v => setMode(v as OwnershipMode)}
                    data={[
                        { value: 'ours',   label: 'Наш' },
                        { value: 'market', label: 'Рынок' },
                        { value: 'both',   label: 'Оба' },
                    ]}
                />
            )}
            {visiblePflow.length > 0 && <>
                <SectionHeader title="PFLOW" />
                <Accordion variant="separated" multiple>
                    {visiblePflow.map(g => (
                        <GlossaryGroupView key={g.label} group={g} mode={mode} onNavigate={onNavigate} discussCounts={discussCounts} />
                    ))}
                </Accordion>
            </>}
            {visibleSvcflow.length > 0 && <>
                <SectionHeader title="SVCFLOW" />
                <Accordion variant="separated" multiple>
                    {visibleSvcflow.map(g => (
                        <GlossaryGroupView key={g.label} group={g} mode={mode} onNavigate={onNavigate} discussCounts={discussCounts} />
                    ))}
                </Accordion>
            </>}
            {visibleDflow.length > 0 && <>
                <SectionHeader title="DFLOW" />
                <Accordion variant="separated" multiple>
                    {visibleDflow.map(g => (
                        <GlossaryGroupView key={g.label} group={g} mode={mode} onNavigate={onNavigate} discussCounts={discussCounts} />
                    ))}
                </Accordion>
            </>}
            {visiblePflow.length === 0 && visibleSvcflow.length === 0 && visibleDflow.length === 0 && (
                <Text fz="sm" c="dimmed">Ничего не найдено.</Text>
            )}
        </Stack>
    );
}

/**
 * Счётчик группы: в режиме «Оба» при наличии рыночных — раздельно «наши|рынок»
 * (например «2|1»), иначе — простое число уже отфильтрованных items.
 */
function groupCounter(group: GlossaryGroup, mode: OwnershipMode): string {
    if (mode === 'both') {
        const market = group.items.filter(i => i.market).length;
        if (market > 0) return `${group.items.length - market}|${market}`;
    }
    return String(group.items.length);
}

function GlossaryGroupView({ group, mode, onNavigate, discussCounts }: {
    group: GlossaryGroup;
    mode: OwnershipMode;
    onNavigate?: () => void;
    discussCounts?: Map<string, number>;
}): JSX.Element {
    return (
        <Accordion.Item value={group.label}>
            <Accordion.Control>
                <Group justify="space-between" wrap="nowrap">
                    <Text fz="sm" fw={500}>{group.label}</Text>
                    <Text fz="xs" c="dimmed" title="наши|рынок">{groupCounter(group, mode)}</Text>
                </Group>
            </Accordion.Control>
            <Accordion.Panel>
                <Stack gap={2}>
                    {group.items.map((item, i) => (
                        <EntityRow key={i} item={item} onNavigate={onNavigate}
                            discussCount={discussCounts?.get(`${item.entity.type}."${item.entity.name}"`) ?? 0} />
                    ))}
                </Stack>
            </Accordion.Panel>
        </Accordion.Item>
    );
}

/**
 * Строка сущности в Справочнике. Клик открывает боковую панель `EntityCard`
 * (через `ui-store.openCard`) — так же, как клик по entity-блоку внутри
 * карточки нарратива (см. `NarrativeBlockView`). На mobile дополнительно
 * закрывает sidebar-drawer через `onNavigate`.
 */
function EntityRow({ item, onNavigate, discussCount = 0 }: {
    item: GlossaryGroup['items'][number];
    onNavigate?: () => void;
    /** Число открытых обсуждений — бейдж 💬. */
    discussCount?: number;
}): JSX.Element {
    const openCard = useUiStore(s => s.openCard);
    const handleClick = (): void => {
        openCard(item.entity);
        onNavigate?.();
    };
    return (
        <Box
            component="button"
            type="button"
            onClick={handleClick}
            className="pflow-entity-trigger"
            title={item.entity.canvas ? `в Канвасе «${item.entity.canvas}»` : undefined}
            style={{
                appearance: 'none',
                background: 'none',
                border: 'none',
                textAlign: 'left',
                cursor: 'pointer',
                color: 'inherit',
                padding: '2px 6px',
                borderRadius: 4,
                display: 'block',
                width: '100%',
            }}
        >
            <Group gap={4} wrap="nowrap">
                <Text fz="xs" truncate style={{ minWidth: 0 }}>«{item.entity.name}»</Text>
                {discussCount > 0 && (
                    <Text fz="xs" c="blue.6" style={{ flexShrink: 0 }} title="Открытые обсуждения">
                        💬{discussCount}
                    </Text>
                )}
            </Group>
        </Box>
    );
}

function filterGroups(groups: GlossaryGroup[], f: string, mode: OwnershipMode): GlossaryGroup[] {
    return groups
        .map(g => ({
            ...g,
            items: g.items
                .filter(i => mode === 'both' || (mode === 'market') === (i.market === true))
                .filter(i => !f || i.entity.name.toLowerCase().includes(f)),
        }))
        .filter(g => g.items.length > 0)
        .map(g => ({ ...g, count: g.items.length }));
}

// ─── shared ───────────────────────────────────────────────────────────

function SectionHeader({ title }: { title: string }): JSX.Element {
    return (
        <Text fz="xs" c="dimmed" fw={600} tt="uppercase" style={{ letterSpacing: 1 }} mt={4}>
            {title}
        </Text>
    );
}

interface AnchorLinkProps {
    anchor:  string;
    icon:    string;
    label:   string;
    title?:  string;
    onNavigate?: () => void;
    compact?: boolean;
}

function AnchorLink({ anchor, icon, label, title, onNavigate, compact = false }: AnchorLinkProps): JSX.Element {
    const triggerFlash = useUiStore(s => s.triggerFlash);
    const padding = compact ? '2px 0' : '4px 8px';

    const handleClick = (e: React.MouseEvent): void => {
        e.preventDefault();
        // Не пишем якорь в URL (replaceState без hash), но триггерим scroll сами —
        // иначе при повторном клике на тот же anchor scroll не сработает.
        const el = document.getElementById(anchor);
        if (el) {
            el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
        triggerFlash(anchor);
        onNavigate?.();
    };

    return (
        <Box
            component="a"
            href={`#${anchor}`}
            onClick={handleClick}
            style={{
                textDecoration: 'none',
                color: 'inherit',
                padding,
                borderRadius: 4,
                display: 'block',
            }}
            title={title}
        >
            <Group gap={6} wrap="nowrap">
                {icon !== '' && <Text fz="xs" style={{ width: 14 }}>{icon}</Text>}
                <Text fz={compact ? 'xs' : 'sm'} truncate>{label}</Text>
            </Group>
        </Box>
    );
}
